import { ArrowLeft, Trash2, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";

const DeleteAccount = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-8">
        <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to App
        </Link>

        <div className="space-y-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2">Delete Your Account</h1>
            <p className="text-muted-foreground">Instructions for permanently deleting your Evolve account and data.</p>
          </div>

          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-destructive">Warning: This action is permanent</p>
              <p className="text-sm text-muted-foreground mt-1">
                Once you delete your account, all your data will be permanently removed and cannot be recovered.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground">How to Delete Your Account</h2>
            <ol className="space-y-4">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  1
                </span>
                <div>
                  <p className="font-medium text-foreground">Open the Evolve app</p>
                  <p className="text-sm text-muted-foreground">
                    Launch the app on your device and sign in to your account.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  2
                </span>
                <div>
                  <p className="font-medium text-foreground">Go to Profile tab</p>
                  <p className="text-sm text-muted-foreground">Tap the Profile icon in the bottom navigation bar.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  3
                </span>
                <div>
                  <p className="font-medium text-foreground">Open Settings</p>
                  <p className="text-sm text-muted-foreground">
                    Tap the gear icon (⚙️) in the top right corner to open Settings.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  4
                </span>
                <div>
                  <p className="font-medium text-foreground">Scroll to Danger Zone</p>
                  <p className="text-sm text-muted-foreground">
                    Scroll down to find the "Danger Zone" section at the bottom.
                  </p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  5
                </span>
                <div>
                  <p className="font-medium text-foreground">Tap "Delete Account"</p>
                  <p className="text-sm text-muted-foreground">Tap the red Delete Account button.</p>
                </div>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium">
                  6
                </span>
                <div>
                  <p className="font-medium text-foreground">Confirm deletion</p>
                  <p className="text-sm text-muted-foreground">
                    A confirmation dialog will appear. Tap "Delete" to permanently remove your account.
                  </p>
                </div>
              </li>
            </ol>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              What Data Will Be Deleted
            </h2>
            <p className="text-muted-foreground">
              When you delete your account, the following data will be permanently removed:
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Profile information (name, avatar, settings)
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Chat history and conversations
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Workout sessions and exercise data
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Nutrition logs and meal history
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Weight history and progress tracking
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Badges and achievements
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Friend connections
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-destructive" />
                Personal records (PRs)
              </li>
            </ul>
          </div>

          <div className="border-t border-border pt-6">
            <h2 className="text-xl font-semibold text-foreground mb-3">Need Help?</h2>
            <p className="text-muted-foreground">
              If you have questions or need assistance with account deletion, please contact us at{" "}
              <a href="mailto:support@evolvewithcoach.com" className="text-primary hover:underline">
                support@evolvewithcoach.com
              </a>
            </p>
          </div>

          {/* Legal Links */}
          <div className="border-t border-border pt-6 space-y-2">
            <h2 className="text-xl font-semibold text-foreground mb-3">Legal</h2>
            <div className="flex flex-col gap-2">
              <a
                href="https://evolveaicoach.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm"
              >
                Privacy Policy
              </a>
              <a
                href="https://www.apple.com/legal/internet-services/itunes/dev/stdeula/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline text-sm"
              >
                Terms of Use (EULA)
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
